import React, { useState, useRef, useEffect } from 'react';
import { useLocation, useParams } from 'react-router-dom';
import { useDispatch } from 'react-redux';
import { X } from 'lucide-react';
import { showAlert } from '../../../slice/AlertSlice';
import { Variants } from '@/types/common';
import { buildRoute } from '../../../config/routes';
import { Typography } from '@/components/ui/typography';
import { Button } from '@/components/ui/button';
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogFooter
} from '@/components/ui/dialog';
import {
  Drawer,
  DrawerClose,
  DrawerContent,
  DrawerTitle
} from '@/components/ui/drawer';
import { useMediaQuery } from '@/hooks/useMediaQuery';
import { DESKTOP_BREAKPOINT_QUERY } from '@/config/ui.constants';
import { ImageAssetType } from './carousel.types';

/**
 * The modal only renders the service name (header / button label), so the
 * prop is narrowed to the shared subset of cart-line `ServiceInfo` and
 * catalog `ProductService` that both callers (asset-view-modal, CartDialogs)
 * already provide.
 */
type ServiceLabel = { name: string };
import AssetSupportIcon from '../../property-assets-management/property-asset-view/assets-action-bar/icons/AssetSupportIcon';
import AssetEditIcon from '../../property-assets-management/property-asset-view/assets-action-bar/icons/AssetEditIcon';
import { Label } from '@/components/ui/label';
import AssetCardList from './asset-card-list';
import { useSendNotificationMutation } from '../../properties/services/propertyApi';
import { Textarea } from '@/components/ui/textarea';

type AssetRequestModalProps = {
  show: boolean;
  onHide: () => void;
  onSupportSubmit?: (details: string) => void;
  onServiceSubmit?: (details: string) => Promise<void>;
  assetData: ImageAssetType[];
  requestType: 'service' | 'support';
  propertyAddress: string;
  service?: ServiceLabel | null;
  isLoader?: boolean;
  mode?: 'create' | 'edit';
  initialDescription?: string;
  onRemoveFromCart?: () => void;
};

type ModalDetailsType = {
  title: string;
  addressLine: string;
  helperText?: string;
};

export default function AssetRequestModal({
  show,
  onHide,
  onSupportSubmit = () => {},
  onServiceSubmit = async () => {},
  assetData,
  requestType = 'support',
  propertyAddress,
  isLoader = false,
  service = null,
  mode = 'create',
  initialDescription = '',
  onRemoveFromCart
}: AssetRequestModalProps) {
  const location = useLocation();
  const { propertyUUID: routePropertyUUID, photosetUUID: routePhotosetUUID } =
    useParams();
  const dispatch = useDispatch();
  const [sendNotification] = useSendNotificationMutation();
  const inputRef = useRef<HTMLTextAreaElement | null>(null);
  const orderId = (location?.state as Record<string, string>)?.orderUUID;
  // Sub-lg the modal is a bottom sheet (QA round 5, spec §1.11) — same lg
  // boundary as the Sync / Order Service sheets.
  const isDesktop = useMediaQuery(DESKTOP_BREAKPOINT_QUERY);

  const buildPhotosetUrl = (asset: ImageAssetType): string | undefined => {
    const propertyUUID = asset.propertyUUID || routePropertyUUID;
    if (!propertyUUID) return undefined;
    return `${window.location.origin}${buildRoute.propertyAssets(
      propertyUUID,
      { photosetUUID: asset.photosetUUID },
    )}`;
  };

  /**
   * DV-3139: the photoset link for a general support request — one with no photos selected.
   *
   * Those requests sent no link at all, so the "Photoset URL" field on the HubSpot ticket was
   * always empty and support had no route back to the property. There is no asset to derive it
   * from here, so it comes from where the user actually is: the photoset they were viewing, or
   * the property's assets page when they were not inside one.
   */
  const buildContextPhotosetUrl = (): string | undefined => {
    if (!routePropertyUUID) return undefined;
    return `${window.location.origin}${buildRoute.propertyAssets(routePropertyUUID, {
      photosetUUID: routePhotosetUUID,
    })}`;
  };
  const [inputMap, setInputMap] = useState<Record<string, string>>({});
  const [assetList, setAssetList] = useState<ImageAssetType[]>(assetData);
  const [content, setContent] = useState('');
  const [isSubmitting, setIsSubmitting] = useState(false);

  const isEditMode = mode === 'edit';

  const getModalTitle = () => {
    if (isEditMode) return `Edit ${service?.name || 'Order'}`;
    if (service) return service.name;
    return 'Request Support for Assets';
  };

  const getModalDetails = (): ModalDetailsType => {
    if (service || isEditMode) {
      return {
        title: getModalTitle(),
        addressLine: propertyAddress
      };
    }
    return {
      title: getModalTitle(),
      addressLine: propertyAddress,
      helperText:
        'Please provide a brief description and our team will respond shortly to confirm the request.'
    };
  };

  const modalDetails: ModalDetailsType = getModalDetails();

  useEffect(() => {
    if (show) {
      setAssetList(assetData);
      if (isEditMode && initialDescription && assetData[0]?.assetUUID) {
        setInputMap({ [assetData[0].assetUUID]: initialDescription });
      }
    }
  }, [show, assetData, isEditMode, initialDescription]);

  useEffect(() => {
    // Desktop-only: on a phone the focus pops the keyboard over the freshly
    // opened sheet, hiding the image list it exists to show (QA round 5).
    if (show && isDesktop && inputRef.current) {
      const timer = setTimeout(() => inputRef.current?.focus(), 50);
      return () => clearTimeout(timer);
    }
  }, [show, isDesktop]);

  const handleRemoveAsset = (assetUUID: string) => {
    setAssetList((prev) => prev.filter((item) => item.assetUUID !== assetUUID));
    setInputMap((prev) => {
      const updated = { ...prev };
      delete updated[assetUUID];
      return updated;
    });
  };

  const handleAssetInputChange = (assetUUID: string, value: string) => {
    setInputMap((prev) => ({ ...prev, [assetUUID]: value }));
  };

  const handleGeneralInputChange = (
    e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement>
  ) => {
    setContent(e.target.value);
  };

  const resetModalState = () => {
    setInputMap({});
    setAssetList(assetData);
    setIsSubmitting(false);
    setContent('');
  };

  const handleSupportSubmit = async () => {
    setIsSubmitting(true);
    try {
      if (assetList.length > 0) {
        const assetSupportRequests = assetList.map((asset) => ({
          assetNote: inputMap[asset.assetUUID!]!.trim(),
          assetUrl: asset?.caches?.['300w'] ?? '',
          caption: asset?.caption,
          photosetUrl: buildPhotosetUrl(asset),
          propertyAddress,
        }));
        await sendNotification({ action: 'Asset Support', assetSupportRequests }).unwrap();
      } else {
        const assetSupportRequests = [
          {
            assetNote: content.trim(),
            propertyAddress,
            photosetUrl: buildContextPhotosetUrl()
          }
        ];
        await sendNotification({
          action: 'Asset Support',
          orderId,
          content: content.trim(),
          assetSupportRequests
        }).unwrap();
      }
      dispatch(
        showAlert({ type: Variants.SUCCESS, message: 'Request is submitted successfully.' })
      );
      onHide();
      resetModalState();
    } catch {
      dispatch(
        showAlert({
          type: Variants.ERROR,
          message: 'Failed to submit the request. Please try again.'
        })
      );
    } finally {
      setIsSubmitting(false);
    }
  };

  const handleServiceSubmit = async () => {
    const serviceInput = inputMap[assetList[0]?.assetUUID!] || '';
    if (service && serviceInput) await onServiceSubmit(serviceInput);
    onHide();
    resetModalState();
  };

  const handleClose = () => {
    resetModalState();
    onHide();
  };

  const isSubmitDisabled =
    isSubmitting ||
    (!assetList.length && !content.trim()) ||
    (assetList.length > 0 &&
      !assetList.every(
        (asset) => asset.assetUUID && inputMap[asset.assetUUID]?.trim()
      ));

  // Shared pieces between the desktop dialog and the sub-lg sheet, so titles,
  // body branching, and the submit rules can't drift between presentations.
  // The title primitive differs (Radix DialogTitle vs DrawerTitle), so the
  // heading takes it as a parameter — same approach as SyncAndShareModal's
  // tabbedBody.
  const heading = (Title: typeof DialogTitle) => (
    <div className="flex items-start gap-2">
      <span className="mt-1 shrink-0">
        {requestType === 'service' ? <AssetEditIcon /> : <AssetSupportIcon />}
      </span>
      <div className="flex flex-col gap-0.5">
        <Title>
          <Typography variant={isDesktop ? 'heading-lg' : 'heading-md'}>
            {modalDetails.title}
          </Typography>
        </Title>
        <Typography variant="body" className="text-muted-foreground">
          {modalDetails.addressLine}
        </Typography>
      </div>
    </div>
  );

  const body = (
    <>
      {modalDetails.helperText && (
        <Typography variant="body" className="text-muted-foreground">
          {modalDetails.helperText}
        </Typography>
      )}

      {assetList.length > 0 && (
        <AssetCardList
          assetList={assetList}
          inputMap={inputMap}
          inputRef={inputRef}
          requestType={requestType}
          mode={mode}
          onInputChange={handleAssetInputChange}
          onRemoveAsset={handleRemoveAsset}
          onRemoveFromCart={onRemoveFromCart}
          stacked={!isDesktop}
          constrainHeight={isDesktop}
        />
      )}
      {assetList.length === 0 && (
        <div className="flex flex-col gap-1">
          <Label htmlFor="details">Add Details <span className="text-destructive">*</span></Label>
          <Textarea
            id="details"
            name="details"
            rows={3}
            maxLength={350}
            placeholder="Add Input"
            value={content}
            onChange={handleGeneralInputChange}
            ref={inputRef as React.Ref<HTMLTextAreaElement>}
            required
          />
          <span className="text-xs text-muted-foreground text-right">{content.length}/350</span>
        </div>
      )}
    </>
  );

  const cancelButton = (className: string) => (
    <Button
      type="button"
      variant="outline"
      onClick={handleClose}
      className={className}
    >
      Cancel
    </Button>
  );

  const submitButton = (className: string) => (
    <>
      {requestType === 'support' && (
        <Button
          type="button"
          disabled={isSubmitDisabled}
          onClick={handleSupportSubmit}
          className={className}
        >
          {isSubmitting && (
            <span className="h-4 w-4 animate-spin rounded-full border-2 border-current border-t-transparent" />
          )}
          Submit request
        </Button>
      )}
      {requestType === 'service' && (
        <Button
          type="button"
          disabled={isSubmitDisabled}
          onClick={handleServiceSubmit}
          className={className}
        >
          {isLoader && (
            <span className="h-4 w-4 animate-spin rounded-full border-2 border-current border-t-transparent" />
          )}
          {isEditMode ? 'Save Changes' : 'Add to Cart'}
        </Button>
      )}
    </>
  );

  // Sub-lg: bottom sheet (QA round 5, spec §1.11) — drawer chrome matching
  // the Order Service sheet: header with an inline close X, scrolling body,
  // bordered footer with full-width stacked CTAs (submit above cancel).
  // Unlike the Order Service sheet, a tap on the scrim closes this one
  // (Steffon, QA round 5 follow-up) — with the drag handle and X it's the
  // standard bottom-sheet gesture set; only the desktop dialog keeps its
  // outside-tap protection for typed notes.
  if (!isDesktop) {
    return (
      <Drawer open={show} onOpenChange={(open) => !open && handleClose()}>
        <DrawerContent
          data-testid="asset-request-sheet"
          className="max-h-[92dvh]"
        >
          <div className="flex shrink-0 items-start justify-between gap-2 border-b border-border px-4 py-3">
            {heading(DrawerTitle)}
            <DrawerClose
              aria-label="Close"
              className="inline-flex h-11 w-11 shrink-0 items-center justify-center rounded-md text-muted-foreground hover:text-foreground"
            >
              <X className="h-5 w-5" />
            </DrawerClose>
          </div>
          <div className="flex min-h-0 flex-1 flex-col gap-4 overflow-y-auto px-4 py-4">
            {body}
          </div>
          {/* Footer surface stays the sheet's own white — hairline border-t
              only, matching the Order Service sheet (Steffon, round 5
              follow-up: the muted fill read as a gray slab). */}
          <div className="flex shrink-0 flex-col gap-2 border-t border-border p-4">
            {submitButton('w-full')}
            {cancelButton('w-full')}
          </div>
        </DrawerContent>
      </Drawer>
    );
  }

  return (
    <Dialog open={show} onOpenChange={(open) => !open && handleClose()}>
      <DialogContent
        className="max-w-[710px] rounded-lg overflow-hidden shadow-modal p-0"
        onPointerDownOutside={(e) => e.preventDefault()}
      >
        {/* Hairline border-b/border-t frame the body; the footer keeps the
            dialog's white surface (Steffon, round 5 follow-up — the muted
            fill read as a gray slab). Matches the sheet branch above. */}
        <DialogHeader className="flex flex-row justify-between items-start px-6 pt-5 pb-4 border-b border-border">
          {heading(DialogTitle)}
        </DialogHeader>

        <div className="flex flex-col gap-4 px-6 pb-5">{body}</div>

        <DialogFooter className="px-6 py-3 border-t border-border">
          {cancelButton('w-full sm:w-auto')}
          {submitButton('w-full sm:w-auto')}
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
